import { useEffect, useState } from "react";
import { Clock, AlertTriangle } from "lucide-react";

/**
 * 24-hour SLA countdown chip for disputes.
 *
 *  - green  : >12h left
 *  - amber  : 2–12h left
 *  - red    : <2h left OR overdue
 *
 * `startedAt` is an ISO string. `slaHours` defaults to 24.
 * `compact` renders a tiny inline pill (for list rows); default = full chip.
 */
export default function SLAClock({ startedAt, slaHours = 24, compact = false, className = "" }) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 60_000); // refresh every minute
        return () => clearInterval(t);
    }, []);

    if (!startedAt) return null;

    const started = new Date(startedAt).getTime();
    if (Number.isNaN(started)) return null;

    const deadline = started + slaHours * 3600_000;
    const msLeft = deadline - now;
    const overdue = msLeft < 0;
    const absMs = Math.abs(msLeft);
    const hours = Math.floor(absMs / 3600_000);
    const minutes = Math.floor((absMs % 3600_000) / 60_000);
    const label = `${hours}h ${minutes}m`;

    let tone;
    if (overdue)                tone = "red";
    else if (msLeft < 2  * 3600_000) tone = "red";
    else if (msLeft < 12 * 3600_000) tone = "amber";
    else                       tone = "green";

    const toneClass = {
        red:   "bg-red-900/40 text-red-300 border-red-700/40",
        amber: "bg-amber-900/40 text-amber-300 border-amber-700/40",
        green: "bg-emerald-900/40 text-emerald-300 border-emerald-700/40",
    }[tone];

    const Icon = overdue ? AlertTriangle : Clock;

    if (compact) {
        return (
            <span
                data-testid={`sla-chip-${tone}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border uppercase tracking-[0.2em] text-[9px] ${toneClass} ${className}`}
                title={overdue ? "SLA overdue" : "Time left on 24h SLA"}
            >
                <Icon size={9} />
                {overdue ? `+${label} over` : label}
            </span>
        );
    }

    return (
        <div
            data-testid={`sla-clock-${tone}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border uppercase tracking-[0.22em] text-[10px] font-semibold ${toneClass} ${className}`}
            title={overdue ? "SLA overdue" : "Time left on 24h SLA"}
        >
            <Icon size={12} />
            {overdue ? (
                <>
                    SLA overdue · <span className="font-mono normal-case tracking-normal">+{label}</span>
                </>
            ) : (
                <>
                    SLA · <span className="font-mono normal-case tracking-normal">{label}</span> left
                </>
            )}
        </div>
    );
}
